import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import * as React from "react";
import { toast } from "sonner";
import * as z from "zod";
import {
  GuardrailCard,
  InfoBanner,
  StatusStrip,
  actionLabel,
} from "../guardrails/-components";
import type { GuardrailDraft, GuardrailDrafts } from "../guardrails/-components";
import type {
  GatewayKeyGuardrailItem,
  GatewayKeyGuardrailUpdateItem,
} from "@/lib/http/gatewaykeys";
import type { GuardrailAction, GuardrailCatalogItem } from "@/lib/http/guardrails";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  gatewayKeyGuardrailsQueryOptions,
  useGatewayKeyGuardrails,
  useUpdateGatewayKeyGuardrails,
} from "@/lib/queries/gateway-keys";
import { guardrailCatalogQueryOptions } from "@/lib/queries/guardrails";
import { useResourceAccessPermission } from "@/lib/perms";
import { cn } from "@/lib/utils";

const paramsSchema = z.object({
  id: z.uuid(),
});

export const Route = createFileRoute("/_app/gateway-keys/$id")({
  component: RouteComponent,
  params: {
    parse: paramsSchema.parse,
  },
  loader({ context: { queryClient }, params: { id } }) {
    queryClient.ensureQueryData(gatewayKeyGuardrailsQueryOptions(id));
    queryClient.ensureQueryData(guardrailCatalogQueryOptions());
  },
  staticData: {
    title: "Gateway Keys",
    isProblematicScrollPage: true,
    permissionGuards: [
      {
        resource: "gateway_keys",
      },
    ],
  },
  pendingComponent: () => (
    <div className="flex h-full items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
    </div>
  ),
});

type KeyDraft = GuardrailDraft & {
  feature_id: number;
  apply_to: string;
};

type KeyDrafts = Partial<Record<string, KeyDraft>>;

function toCatalogItem(item: GatewayKeyGuardrailItem): GuardrailCatalogItem {
  return {
    key: item.key,
    feature_id: item.feature_id,
    display_name: item.display_name,
    description: item.description,
    supported_actions: item.supported_actions,
    supports_spans: item.supports_spans,
    supports_output_direction: item.supports_output_direction,
    typical_latency_ms: item.typical_latency_ms,
    default_settings: item.settings,
    settings_schema:
      item.settings_schema as GuardrailCatalogItem["settings_schema"],
  };
}

function draftFromItem(item: GatewayKeyGuardrailItem): KeyDraft {
  return {
    enabled: item.enabled,
    action: item.action,
    settings: { ...item.settings },
    feature_id: item.feature_id,
    apply_to: item.apply_to,
  };
}

function draftsFromItems(items: Array<GatewayKeyGuardrailItem>): KeyDrafts {
  const next: KeyDrafts = {};
  for (const item of items) {
    next[item.key] = draftFromItem(item);
  }
  return next;
}

function serializeDrafts(drafts: KeyDrafts) {
  return JSON.stringify(
    Object.entries(drafts)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, draft]) => [
        key,
        draft
          ? {
              enabled: draft.enabled,
              action: draft.action,
              settings: draft.settings,
              apply_to: draft.apply_to,
            }
          : null,
      ]),
  );
}

const reviewPill: Record<GuardrailAction, string> = {
  block:
    "border-red-200 bg-red-50 text-red-600 dark:border-red-800 dark:bg-red-950/50 dark:text-red-400",
  redact:
    "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950/50 dark:text-amber-400",
  flag: "border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-800 dark:bg-sky-950/50 dark:text-sky-300",
};

function RouteComponent() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const catalogQuery = useQuery(guardrailCatalogQueryOptions());
  const guardrailsQuery = useGatewayKeyGuardrails(id);
  const canUpdate = useResourceAccessPermission("gateway_keys", ["update"]);
  const saveMutation = useUpdateGatewayKeyGuardrails();

  const [drafts, setDrafts] = React.useState<KeyDrafts>({});
  const [baseline, setBaseline] = React.useState("");
  const [reviewOpen, setReviewOpen] = React.useState(false);

  React.useLayoutEffect(() => {
    const items = guardrailsQuery.data?.items;
    if (!items) return;
    const next = draftsFromItems(items);
    setDrafts(next);
    setBaseline(serializeDrafts(next));
    setReviewOpen(false);
  }, [id, guardrailsQuery.data]);

  function patchDraft(key: string, patch: Partial<KeyDraft>) {
    setDrafts((prev) => {
      const current = prev[key];
      if (!current) return prev;
      return { ...prev, [key]: { ...current, ...patch } };
    });
  }

  const items = guardrailsQuery.data?.items ?? [];
  const catalogItems = items.map(toCatalogItem);
  const isDirty = serializeDrafts(drafts) !== baseline;

  function handleSave() {
    const payload: Array<GatewayKeyGuardrailUpdateItem> = [];
    for (const item of items) {
      const draft = drafts[item.key];
      if (!draft) continue;
      payload.push({
        feature_id: draft.feature_id,
        is_enabled: draft.enabled,
        action: draft.action,
        apply_to: draft.apply_to,
        settings: draft.settings,
      });
    }

    saveMutation.mutate(
      { id, items: payload },
      {
        onSuccess: () => {
          setReviewOpen(false);
          toast.success("Guardrails saved");
        },
        onError: (error) => {
          toast.error(error.message || "Failed to save guardrails");
        },
      },
    );
  }

  if (guardrailsQuery.isPending) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (guardrailsQuery.isError) {
    return (
      <div className="w-full">
        <h1 className="mb-6 text-2xl font-semibold tracking-tight">
          Guardrails
        </h1>
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle>Could not load guardrails</CardTitle>
            <CardDescription className="text-destructive">
              {guardrailsQuery.error.message ||
                "This gateway key’s guardrails are unavailable."}
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const enums = catalogQuery.data?.enums ?? {};

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto">
        <InfoBanner />
        <div className="flex justify-end">
          <StatusStrip drafts={drafts as GuardrailDrafts} catalog={catalogItems} />
        </div>

        <div className="space-y-3 pb-4">
          {items.map((item) => {
            const draft = drafts[item.key];
            if (!draft) return null;
            return (
              <GuardrailCard
                key={item.id}
                item={toCatalogItem(item)}
                draft={draft}
                enums={enums}
                onToggle={(enabled) => patchDraft(item.key, { enabled })}
                onActionChange={(action: GuardrailAction) =>
                  patchDraft(item.key, { action })
                }
                onSettingChange={(settingKey, value) =>
                  patchDraft(item.key, {
                    settings: {
                      ...draft.settings,
                      [settingKey]: value,
                    },
                  })
                }
              />
            );
          })}
        </div>
      </div>

      <div className="-mx-4 -mb-4 flex shrink-0 justify-end gap-2 border-t bg-background px-4 py-4 md:-mx-6 md:-mb-6 md:px-6">
        <Button
          variant="outline"
          onClick={() => navigate({ to: "/gateway-keys" })}
        >
          Cancel
        </Button>
        <Button
          disabled={!canUpdate.data}
          onClick={() => setReviewOpen(true)}
        >
          Review Changes
        </Button>
      </div>

      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Guardrails Review</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {items.map((item) => {
              const draft = drafts[item.key];
              if (!draft) return null;
              return (
                <div
                  key={item.id}
                  className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 px-4 py-3 dark:border-border"
                >
                  <span className="text-sm text-foreground">
                    {item.display_name}
                  </span>
                  {draft.enabled ? (
                    <span
                      className={cn(
                        "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium",
                        reviewPill[draft.action],
                      )}
                    >
                      {actionLabel(draft.action)}
                    </span>
                  ) : (
                    <span className="inline-flex items-center rounded-full border border-zinc-200 bg-zinc-50 px-2.5 py-0.5 text-xs font-medium text-zinc-500 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-400">
                      Off
                    </span>
                  )}
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewOpen(false)}>
              Keep Editing
            </Button>
            <Button
              disabled={saveMutation.isPending || !isDirty}
              onClick={handleSave}
            >
              {saveMutation.isPending ? "Saving…" : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
